/**
 * Created by neo on 10.01.2026
 * Community Wall Post model
 */
import { makeAutoObservable } from 'mobx';
import { Audited, createAuditedFields } from '../../Audited';
import { Media, createMedia, getMediaSmallest } from '../../Media/Media';
import { HttpBackend } from '../../../Services/Http/HttpBackend';
import { Pageable } from '../../Interfaces/Pageable';

export type CommunityWallPostType = 'LOCATION_REACHED' | 'TEAM_MILESTONE' | 'CHALLENGE_COMPLETED' | 'GENERAL';

export type ReactionStats = {
  postId: string;
  totalCount: number;
  counts: Record<string, number>;
  myReaction?: string | null;
};

export const REACTION_TYPES = {
  like: '❤️',
  fire: '🔥',
  clap: '👏',
  celebrate: '🎉',
  strong: '💪',
} as const;

export type CommunityWallPostRequest = Pageable & {
  challengeId?: string;
  locationId?: string;
  fromLocationId?: string;
  toLocationId?: string;
};

export type CommunityWallPost = Audited & {
  gymId: string;
  challengeId: string;
  teamId?: string;
  memberId?: string;
  customerId: string;
  athleteId: string;
  postType: CommunityWallPostType;
  locationId?: string;
  fromLocationId?: string;
  toLocationId?: string;
  text: string;
  medias: Media[];
  authorUsername?: string;
  authorProfileImage?: Media;
  ticketId?: string;
  readonly hasMedia: boolean;
  readonly firstMedia: Media | undefined;
  readonly authorProfileImageUri: string | undefined;
  setReaction(reactionType: string): Promise<ReactionStats>;
  removeReaction(): Promise<ReactionStats>;
};

export function createCommunityWallPost(json?: Partial<CommunityWallPost>): CommunityWallPost {
  const audited = createAuditedFields(json);
  return makeAutoObservable({
    ...audited,
    gymId: json?.gymId ?? '',
    challengeId: json?.challengeId ?? '',
    teamId: json?.teamId,
    memberId: json?.memberId,
    customerId: json?.customerId ?? '',
    athleteId: json?.athleteId ?? '',
    postType: json?.postType ?? 'LOCATION_REACHED',
    locationId: json?.locationId,
    fromLocationId: json?.fromLocationId,
    toLocationId: json?.toLocationId,
    text: json?.text ?? '',
    medias: ((json?.medias as any) ?? []).map((m: any) => createMedia(m)),
    authorUsername: json?.authorUsername,
    authorProfileImage: json?.authorProfileImage ? createMedia(json.authorProfileImage as any) : undefined,
    ticketId: json?.ticketId,

    get hasMedia(): boolean {
      return this.medias.length > 0;
    },

    get firstMedia(): Media | undefined {
      return this.medias[0];
    },

    get authorProfileImageUri(): string | undefined {
      return this.authorProfileImage ? getMediaSmallest(this.authorProfileImage) : undefined;
    },

    setReaction(reactionType: string): Promise<ReactionStats> {
      return HttpBackend.post(`/coach-program/engagement/community-wall/${this.id}/reactions/${reactionType}`);
    },

    removeReaction(): Promise<ReactionStats> {
      return HttpBackend.delete(`/coach-program/engagement/community-wall/${this.id}/reactions`);
    },
  });
}

export const CommunityWallPostApi = {
  find(request?: Partial<CommunityWallPostRequest>): Promise<CommunityWallPost[]> {
    return HttpBackend.get('/coach-program/engagement/community-wall', request).then((response) =>
      (response ?? []).map((r: any) => createCommunityWallPost(r)),
    );
  },

  findByChallenge(challengeId: string, request?: Partial<Pageable>): Promise<CommunityWallPost[]> {
    return HttpBackend.get(
      `/coach-program/engagement/community-wall/challenges/${challengeId}?sort=createdAt,desc`,
      request,
    ).then((response) => (response ?? []).map((r: any) => createCommunityWallPost(r)));
  },

  findByLocation(challengeId: string, locationId: string, request?: Partial<Pageable>): Promise<CommunityWallPost[]> {
    return HttpBackend.get(
      `/coach-program/engagement/community-wall/challenges/${challengeId}/locations/${locationId}`,
      request,
    ).then((response) => (response ?? []).map((r: any) => createCommunityWallPost(r)));
  },

  findOne(postId: string): Promise<CommunityWallPost | undefined> {
    return HttpBackend.get(`/coach-program/engagement/community-wall/${postId}`).then((response) =>
      response ? createCommunityWallPost(response) : undefined,
    );
  },

  getReactionStats(postId: string): Promise<ReactionStats> {
    return HttpBackend.get(`/coach-program/engagement/community-wall/${postId}/reactions`);
  },
};
